import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { BASE_API_URL } from '../utils/fetchApi';
import { hashPassword } from '../utils/helpers';

type TemperatureUnit = 'Celsius' | 'Fahrenheit';

interface UserContextType {
  isLoggedIn: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  unit: TemperatureUnit;
  toggleUnit: () => void;
  updatePassword: (username: string, newPassword: string) => Promise<void>; // ÚJ
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const UserProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(!!localStorage.getItem('authToken'));
  const [unit, setUnit] = useState<TemperatureUnit>('Celsius');

  const login = async (username: string, password: string): Promise<void> => {
    const pwhash = await hashPassword(password);
    try {
      const response = await fetch(BASE_API_URL, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          operation: 'get_jw_token',
          payload: {
            username,
            pwhash: pwhash,
          },
        }),
      });

      if (!response.ok) {
        throw new Error('Login failed');
      }

      let token = await response.text();

      localStorage.setItem('username', username);

      if (token && token !== 'null') {
        token = token.replace(/^"|"$/g, "");
        localStorage.setItem('authToken', token);
        setIsLoggedIn(true);
      } else {
        throw new Error('invalidCredentials');
      }
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const updatePassword = async (username: string, newPassword: string): Promise<void> => {
    const pwhash = await hashPassword(newPassword);
    const response = await fetch(BASE_API_URL, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        operation: 'get_jw_token',
        payload: {
          username,
          pwhash,
        },
      }),
    });
  
    if (!response.ok) {
      throw new Error('Failed to update token after password change');
    }

    let token = await response.text();
    if (token && token !== 'null') {
      token = token.replace(/^"|"$/g, "");
      localStorage.setItem('authToken', token);
      setIsLoggedIn(true);
    } else {
      throw new Error('invalidCredentials');
    }
  };

  const logout = (): void => {
    localStorage.removeItem('authToken');
    setIsLoggedIn(false);
  };

  useEffect(() => {
    setIsLoggedIn(!!localStorage.getItem('authToken'));
  }, []);

  const toggleUnit = () => setUnit((prevUnit) => (prevUnit === 'Celsius' ? 'Fahrenheit' : 'Celsius'));

  return (
    <UserContext.Provider value={{ isLoggedIn, login, logout, unit, toggleUnit, updatePassword }}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = (): UserContextType => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};
